import * as React from "react";

type SetDrawerFunction = (
  key: string,
  title: React.ReactNode,
  content: React.ReactNode,
  width?: number | undefined,
) => void;

type SetDrawerFunction2 = (
  key: string,
  title: React.ReactNode,
  content: React.ReactNode,
  width?: number | undefined,
) => void;

interface DrawerContextType {
  persistent: DrawerData;
  temporary: DrawerData;
  setPersistent: SetDrawerFunction;
  closePersistent: () => void;
  setTemporary: SetDrawerFunction2;
  closeTemporary: () => void;
}

interface DrawerData {
  isOpen: boolean;
  key: string;
  title: React.ReactNode;
  content: React.ReactNode;
  width: number | undefined;
  _close: () => void;
  setDrawer: SetDrawerFunction;
}

function useDrawerData() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [key, setKey] = React.useState("");
  const [title, setTitle] = React.useState<React.ReactNode>(null);
  const [width, setWidth] = React.useState<number | undefined>(undefined);
  const [content, setContent] = React.useState<React.ReactNode>(null);

  function setDrawer(_key: string, title: React.ReactNode, content: React.ReactNode, width?: number) {
    if (_key === key) {
      _close();
      return;
    }

    setKey(_key);
    setTitle(title);
    setIsOpen(true);
    setWidth(width);
    setContent(content);
  }

  function _close() {
    setKey("");
    setIsOpen(false);
  }

  return { isOpen, key, title, content, width, _close, setDrawer };
}

export function useInitDrawerContext(): DrawerContextType {
  const persistent = useDrawerData();
  const temporary = useDrawerData();

  function setPersistent(_key: string, title: React.ReactNode, content: React.ReactNode, width: number = 400) {
    closeTemporary();

    if (_key === persistent.key) {
      closePersistent();
      return;
    }

    try {
      const htmlElement = document.querySelector("html")!;
      htmlElement.style.setProperty(`--cpln-drawer-persistent-width`, `${width}px`);
    } catch (e) {}

    persistent.setDrawer(_key, title, content, width);
  }

  function setTemporary(_key: string, title: React.ReactNode, content: React.ReactNode, width: number = 400) {
    if (_key === temporary.key) {
      closeTemporary();
      return;
    }

    try {
      const htmlElement = document.querySelector("html")!;
      htmlElement.style.setProperty(`--cpln-drawer-temporary-width`, `${width}px`);
    } catch (e) {}

    temporary.setDrawer(_key, title, content, width);
  }

  function closeTemporary() {
    try {
      const htmlElement = document.querySelector("html")!;
      htmlElement.style.setProperty(`--cpln-drawer-temporary-width`, "0px");
    } catch (e) {}
    temporary._close();
  }

  function closePersistent() {
    try {
      const htmlElement = document.querySelector("html")!;
      htmlElement.style.setProperty(`--cpln-drawer-persistent-width`, "0px");
    } catch (e) {}
    closeTemporary();
    persistent._close();
  }

  return {
    persistent,
    temporary,
    setPersistent,
    closePersistent,
    setTemporary,
    closeTemporary,
  };
}

export const DrawerContext = React.createContext<DrawerContextType>(null as any);

export function useDrawerContext() {
  return React.useContext(DrawerContext);
}
